import { ChallengeUrls } from "@/modules/auth/common/constants";
import type { ILoginRequest } from "@/modules/auth/common/types";
import { useLoginMutation } from "@/modules/auth/services";
import { setChallenge, setVerificationId } from "@/modules/auth/state";
import { loginValidationSchema } from "@/modules/auth/validations";
import { AppPaths, CloudflareAsset } from "@/shared/common/enums";
import { CloudFlareImage, Notification } from "@/shared/components";
import { useAppDispatch, useAppSelector, useNotify } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import type { TFormData } from "@ab.uitools/ui-library/types/globalTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { QrModal } from "./QrModal";

const initialValues = {
  userName: "",
  password: "",
};

export const Login = () => {
  const { t } = useTranslation();
  const isQRLoginAvailable = useAppSelector((state) => state.featureToggle.toggles.isQRLoginAvailable);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showErrorAlertNotification } = useNotify();

  const [isPasswordShow, setIsPasswordShow] = useState<boolean>(false);
  const [isOpenQrModal, setIsOpenQrModal] = useState<boolean>(false);
  const [login, { isLoading }] = useLoginMutation();

  const onCloseQrModal = () => {
    setIsOpenQrModal(false);
  };

  const onOpenQrModal = () => {
    setIsOpenQrModal(true);
  };

  const handleSubmit = (values: TFormData, isDirty: boolean) => {
    if (isDirty) {
      login({ ...values } as ILoginRequest)
        .unwrap()
        .then((res) => {
          const verificationId = res?.challengeData.id;
          const challenge = res?.challengeData.challenge as keyof typeof ChallengeUrls;
          if (verificationId) {
            dispatch(setVerificationId(verificationId));
            dispatch(setChallenge(challenge));
          }
          if (ChallengeUrls[challenge]) {
            navigate(ChallengeUrls[challenge]);
          }
        })
        .catch((err) => {
          showErrorAlertNotification(err);
        });
    }
  };

  return (
    <div id="login-form" className="login">
      <div className="card flexbox flex-direction--column align-items--center full-width p-48">
        <div className="full-width">
          <Heading
            className="mr-12 auth-title"
            size="xsmall"
            weight="bold"
            lineHeight="small"
            data-testid="login-form-title"
          >
            {t("auth.login.title")}
          </Heading>
        </div>
        <Notification className="mt-8 full-width"/>
        <FormContainer
          className="full-width"
          onSubmit={(data, formState) => handleSubmit(data, formState.isDirty)}
          mode="onBlur"
          reValidateMode="onBlur"
          validationScheme={loginValidationSchema({ t })}
          initialValues={initialValues}
          shouldFocusError
        >
          <FormField
            name="userName"
            className="mt-24"
            errorMessageIcon="infoFilled"
            data-testid="login-username-field"
            As={(props) => (
              <Input
                {...props}
                required
                label={t("auth.login.username")}
              />
            )}
          />
          <FormField
            name="password"
            className="mt-24"
            errorMessageIcon="infoFilled"
            data-testid="login-password-field"
            As={(props) => (
              <Input
                {...props}
                type={isPasswordShow ? "text" : "password"}
                required
                label={t("auth.login.password")}
                rightIconProps={{
                  Component: (props: ISVGIconProps) => (
                    <CloudFlareImage
                      {...props}
                      path={isPasswordShow ? CloudflareAsset.EyeClose : CloudflareAsset.EyeOpen}
                    />
                  ),
                  onClick() {
                    setIsPasswordShow((prev: boolean) => !prev);
                  },
                }}
              />
            )}
          />
          <div>
            <Button
              className="full-width mt-8 forget-password-button"
              buttonText={t("auth.login.forgetPassword")}
              onClick={() => navigate(AppPaths.Reset)}
              type="text"
              data-testid="forget-password-username-button"
              data-ga-id="reset-redirect-button"
              data-ga-action="Redirect to reset page"
            />
          </div>
          <Button
            name="login"
            type="primary"
            className="full-width mt-32"
            buttonActionType="submit"
            buttonText={t("auth.login.action")}
            isLoading={isLoading}
            disabled={isLoading}
            data-testid="login-button"
            data-ga-id="login-button"
            data-ga-label="Login"
            data-ga-action="Login action for user login"
          />
          <Button
            name="qr"
            type="secondary"
            className="full-width mt-16"
            buttonText={t("auth.login.qrCodeLogin")}
            onClick={onOpenQrModal}
            disabled={isLoading || !isQRLoginAvailable}
            data-testid="qr-login-button"
            data-ga-id="qr-login-button"
            data-ga-label="QR Login"
            data-ga-action="Open QR Modal"
            iconProps={{
              Component: (props: ISVGIconProps) => (
                <CloudFlareImage {...props} path={CloudflareAsset.QRScanner}/>
              ),
            }}
          />
        </FormContainer>
      </div>
      <QrModal isOpen={isOpenQrModal} onClose={onCloseQrModal}/>
    </div>
  );
};
